import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';

Chart.register(annotationPlugin);

document.addEventListener('DOMContentLoaded', () => {
	console.log('globalStateSB2025.js loaded');
	console.log('Chart.js version:', Chart.version);

	/**
	 * === GLOBAL EXTERNAL TOOLTIP HANDLER ===
	 * Creates a single reusable custom tooltip element per chart.
	 * Handles both show/hide behavior and dynamic positioning.
	 */
	const externalTooltipHandler = (context) => {
		const { chart, tooltip } = context;
		let tooltipEl = chart.canvas.parentNode.querySelector('.chartjs-custom-tooltip');

		// Create tooltip container if it doesn't exist
		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.classList.add('chartjs-custom-tooltip');
			tooltipEl.innerHTML = '<div class="tooltip-body"></div>';
			tooltipEl.style.position = 'absolute';
			tooltipEl.style.pointerEvents = 'none';
			tooltipEl.style.transition = 'all 0.15s ease';
			chart.canvas.parentNode.appendChild(tooltipEl);
		}

		// Hide tooltip when opacity is 0
		if (tooltip.opacity === 0) {
			tooltipEl.style.opacity = 0;
			return;
		}

		const titleLines = tooltip.title || [];
		const bodyLines = tooltip.body?.map(b => b.lines) || [];
		let innerHtml = '';

		if (titleLines.length) {
			innerHtml += `<div class="tooltip-title">${titleLines.join(' ')}</div>`;
		}

		// ✅ Show all items, including "Other"
		bodyLines.forEach(body => {
			const text = body[0];
			if (text) {
				innerHtml += `<div class="tooltip-value">${text}</div>`;
			}
		});

		const tooltipBody = tooltipEl.querySelector('.tooltip-body');
		tooltipBody.innerHTML = innerHtml;

		// Position tooltip relative to chart canvas
		const { offsetLeft: posX, offsetTop: posY } = chart.canvas;
		tooltipEl.style.opacity = 1;
		tooltipEl.style.left = `${posX + tooltip.caretX}px`;
		tooltipEl.style.top = `${posY + tooltip.caretY - 20}px`;
	};

	/**
	 * === GLOBAL TOOLTIP CONFIG ===
	 * Common configuration for all charts using custom tooltips.
	 */
	const tooltipCommon = {
		enabled: false,
		external: externalTooltipHandler
	};

	/**
	 * === CHART CONFIGURATIONS ===
	 * Contains all charts to be rendered when visible.
	 */
	const charts = [
		{
			id: 'tgi_2024',
			type: 'pie',
			data: {
				labels: ['Beauty & Wellness', 'Medical', 'Sport', 'Other'],
				datasets: [{
					data: [20.98, 16.57, 16.28, 46.17],
					backgroundColor: ['#F57BC7', '#06ADEF', '#A3E635', '#EEF1F4'],
					borderWidth: 0,
				}]
			},
			options: {
				responsive: true,
				plugins: {
					legend: { display: false },
					tooltip: {
						...tooltipCommon,
						callbacks: {
							label: ctx => `${ctx.label}`,
							title: () => ''
						}
					}
				},
				animation: { duration: 1200, easing: 'easeOutQuart' }
			}
		},
		{
			id: 'ust_education',
			type: 'line',
			data: {
				labels: ['July', 'August', 'September'],
				datasets: [{
					data: [40, 90, 105],
					borderColor: '#06ADEF',
					backgroundColor: 'rgba(6,173,239,0.1)',
					tension: 0.4,
					pointRadius: 5,
					pointHoverRadius: 7,
					pointBackgroundColor: '#06ADEF',
					pointBorderWidth: 2,
					fill: false,
					borderWidth: 4,
				}]
			},
			options: {
				responsive: true,
				plugins: {
					legend: { display: false },
					tooltip: {
						...tooltipCommon,
						callbacks: {
							label: ctx => '',
							title: ctx => ctx[0].label
						}
					},
					annotation: {
						annotations: {
							increaseLabel: {
								type: 'label',
								xValue: 2,
								yValue: 115,
								backgroundColor: 'transparent',
								color: '#06ADEF',
								font: { weight: 'bold', size: 14 },
								content: '+15%',
								display: true,
								position: 'end',
								yAdjust: -15
							}
						}
					}
				},
				scales: {
					y: { display: false, beginAtZero: true },
					x: { type: 'category', grid: { display: false } }
				},
				animation: { duration: 1500, easing: 'easeOutCubic' }
			}
		},
		{
			id: 'ust_mobile_first',
			type: 'pie',
			data: {
				labels: ['Mobile', 'Other'],
				datasets: [{
					data: [70, 30],
					backgroundColor: ['#06ADEF', '#EAF7FD'],
					borderWidth: 0
				}]
			},
			options: {
				cutout: '0',
				plugins: {
					legend: { display: false },
					tooltip: {
						...tooltipCommon,
						callbacks: {
							label: ctx => `${ctx.label}: ${ctx.formattedValue}%`,
							title: () => ''
						}
					}
				},
				animation: { duration: 1200 }
			}
		},
		{
			id: 'ust_service',
			type: 'doughnut',
			data: {
				labels: ['Self-service', 'Other'],
				datasets: [{
					data: [95, 5],
					backgroundColor: ['#204C8E', '#E7ECF5'],
					borderWidth: 0
				}]
			},
			options: {
				cutout: '0',
				plugins: {
					legend: { display: false },
					tooltip: {
						...tooltipCommon,
						callbacks: {
							label: ctx => `${ctx.label}: ${ctx.formattedValue}%`,
							title: () => ''
						}
					}
				},
				animation: { duration: 1200 }
			}
		}
	];

	/**
	 * === CREATE CHART FUNCTION ===
	 * Dynamically creates and renders a Chart.js chart inside the given element.
	 */
	function createChart(config) {
		const el = document.getElementById(config.id);
		if (!el) return;

		el.innerHTML = '';
		const canvas = document.createElement('canvas');
		el.appendChild(canvas);

		new Chart(canvas, {
			type: config.type,
			data: config.data,
			options: config.options
		});
	}

	/**
	 * === INTERSECTION OBSERVER ===
	 * Watches for charts entering the viewport and initializes them when visible.
	 */
	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				const id = entry.target.getAttribute('id');
				const config = charts.find(c => c.id === id);
				if (config) createChart(config);
			}
		});
	}, { threshold: 0.4 });

	document.querySelectorAll('.chart-component').forEach(el => observer.observe(el));
});
